import { MatchEngine } from '../../matches/domain/engine/match-engine';
import { uniformSets } from '../../matches/domain/engine/match-format';
import { MatchEvent, MatchState, TeamId, allPlayers } from '../../matches/domain/models/match.models';
import { ChartDataBuilder } from '../../stats/domain/chart-data-builder';
import { MvpEngine } from '../../stats/domain/mvp-engine';
import { receivePointWinPct, servePointWinPct } from '../../stats/domain/stat-catalog';
import { StatsEngine } from '../../stats/domain/stats-engine';
import { ChartDefinition, PlayerBadge, PlayerStatsSummary } from '../../stats/domain/stats.models';
import type { RosterEntry, Training } from '../../trainings/domain/training.models';

// Demo data for the landing page. Chart definitions mirror the exact shapes the real
// ChartDataBuilder produces, and the demo match is a fully valid MatchState so the genuine
// match-detail components render it unchanged — only the numbers and names are staged.
// Cast of demo players: Ines, Fabio, Johanna, Pia, Franca, Paul, Leonie, Hella, Theo.

const localFutureIso = (days: number, hour: number, minute = 0): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(hour, minute, 0, 0);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
};

const demoRoster = (): Record<string, RosterEntry> => {
  const names = ['Jonas P.', 'Mira K.', 'Leon S.', 'Ruth A.', 'Sam T.', 'Lina W.', 'Max R.', 'Jana F.', 'Tom H.', 'Emma L.', 'Noah D.', 'Mia G.'];
  return Object.fromEntries(names.map((displayName, index) => {
    const uid = `landing-player-${index + 1}`;
    return [uid, {
      uid,
      status: 'going',
      respondedAt: new Date(Date.now() + index * 1000).toISOString(),
      note: null,
      displayName,
      avatarUrl: null,
      rgx: null,
      category: 'open',
    } satisfies RosterEntry];
  }));
};

const demoTraining = (tid: string, title: string, days: number, hour: number, seriesId: string | null): Training => ({
  tid,
  cid: 'landing-community',
  groupId: 'landing-group',
  title,
  description: 'A focused roundnet session.',
  location: { address: 'Stadtpark, Nürnberg', lat: 49.463, lng: 11.086 },
  startAt: localFutureIso(days, hour),
  endAt: localFutureIso(days, hour + 2),
  rsvpDeadline: null,
  trainerUids: ['landing-trainer'],
  capacity: 16,
  visibility: 'open',
  regionKey: 'de-by',
  seriesId,
  detachedFromSeries: false,
  weatherSnapshot: { tempC: 18, weatherCode: 2, windKph: 12, precipitationProbability: 20, fetchedAt: new Date().toISOString() },
  roster: demoRoster(),
  attendanceSummary: { checkedIn: 8, checkedOut: 0, guests: 1, removed: 0, checkedInPreview: [] },
  status: 'scheduled',
  createdByUid: 'landing-trainer',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const DEMO_TRAININGS: Training[] = [
  demoTraining('landing-training-next', 'Club training', 2, 18, 'landing-weekly-club'),
  demoTraining('landing-training-beginners', 'Beginners', 4, 18, 'landing-weekly-beginners'),
  demoTraining('landing-training-pickup', 'Open pickup', 6, 11, 'landing-weekly-pickup'),
];

export const DEMO_TRAINING = DEMO_TRAININGS[0];

export const DEMO_MOMENTUM: ChartDefinition = {
  id: 'landing-momentum',
  title: 'Momentum Chart',
  description: 'The score gap after every point.',
  type: 'line',
  xAxisLabel: 'Point number',
  yAxisLabel: 'Score differential',
  series: [
    {
      name: 'Set 1',
      points: [
        { x: 1, y: 1, label: '1-0' }, { x: 2, y: 0, label: '1-1' }, { x: 3, y: -1, label: '1-2' },
        { x: 4, y: -2, label: '1-3' }, { x: 5, y: -1, label: '2-3' }, { x: 6, y: 0, label: '3-3' },
        { x: 7, y: 1, label: '4-3' }, { x: 8, y: 2, label: '5-3' }, { x: 9, y: 3, label: '6-3' },
        { x: 10, y: 2, label: '6-4' }, { x: 11, y: 3, label: '7-4' }, { x: 12, y: 4, label: '8-4' },
      ],
    },
    {
      name: 'Set 2',
      points: [
        { x: 1, y: -1, label: '0-1' }, { x: 2, y: -2, label: '0-2' }, { x: 3, y: -1, label: '1-2' },
        { x: 4, y: 0, label: '2-2' }, { x: 5, y: -1, label: '2-3' }, { x: 6, y: 0, label: '3-3' },
        { x: 7, y: 1, label: '4-3' }, { x: 8, y: 0, label: '4-4' }, { x: 9, y: 1, label: '5-4' },
        { x: 10, y: 2, label: '6-4' }, { x: 11, y: 1, label: '6-5' }, { x: 12, y: 2, label: '7-5' },
      ],
    },
  ],
  filters: [],
  sampleSize: 96,
};

export const DEMO_BREAK_SIDEOUT: ChartDefinition = {
  id: 'landing-break-sideout',
  title: 'Break vs Side-Out Map',
  description: 'Each dot is a player.',
  type: 'scatter',
  xAxisLabel: 'Break %',
  yAxisLabel: 'Side-Out %',
  series: [
    {
      name: 'Players',
      color: 'break',
      points: [
        { x: 62, y: 75, label: 'Franca' },
        { x: 48, y: 81, label: 'Leonie' },
        { x: 71, y: 58, label: 'Hella' },
        { x: 55, y: 64, label: 'Theo' },
        { x: 40, y: 52, label: 'Paul' },
        { x: 66, y: 69, label: 'Johanna' },
      ],
    },
  ],
  filters: [],
  sampleSize: 1260,
};

export const DEMO_OUTCOME_MIX: ChartDefinition = {
  id: 'landing-outcome-mix',
  title: 'Break vs side-out',
  description: 'How every point was won.',
  type: 'pie',
  xAxisLabel: 'points',
  series: [
    {
      name: 'Points',
      points: [
        { x: 'Breaks', y: 94, label: 'Breaks' },
        { x: 'Side-Outs', y: 132, label: 'Side-Outs' },
        { x: 'Aces', y: 38, label: 'Aces' },
        { x: 'Dbl faults', y: 18, label: 'Dbl faults' },
      ],
    },
  ],
  filters: [],
  sampleSize: 282,
};

export const DEMO_PRESSURE: ChartDefinition = {
  id: 'landing-pressure',
  title: 'Pressure response',
  description: 'Point win rate by pressure level.',
  type: 'polar',
  series: [
    {
      name: 'You',
      points: [
        { x: 'Low', y: 71 },
        { x: 'Medium', y: 65 },
        { x: 'High', y: 58 },
        { x: 'Extreme', y: 74 },
      ],
    },
  ],
  filters: [],
  sampleSize: 282,
};

// ── Ultimate player cards ────────────────────────────────────────────────────

const demoStats = (overrides: Partial<PlayerStatsSummary>): PlayerStatsSummary => ({
  playerId: 'demo', displayName: 'Demo', matchesPlayed: 0, matchesWon: 0, matchesDrawn: 0, matchWinPercentage: 0,
  pointsPlayed: 0, pointsWon: 0, breakAttempts: 0, breakWins: 0, breakPercentage: 0,
  rallyServePoints: 0, rallyReceivePoints: 0,
  sideOutAttempts: 0, sideOutWins: 0, sideOutPercentage: 0, firstServeFaults: 0, doubleFaults: 0,
  aces: 0, acesReceived: 0, firstServeAces: 0, secondServeAces: 0, faultRate: 0, doubleFaultRate: 0,
  aceRate: 0, aceAgainstRate: 0, offensiveServingPressure: 0, defensiveServingPressure: 0,
  pressurePointWinPercentage: { LOW: 0, MEDIUM: 0, HIGH: 0, EXTREME: 0 },
  pressurePointRecord: {
    LOW: { won: 0, played: 0 }, MEDIUM: { won: 0, played: 0 },
    HIGH: { won: 0, played: 0 }, EXTREME: { won: 0, played: 0 },
  },
  pressureScore: 0, clutchBreakAttempts: 0, clutchBreakWins: 0, clutchBreakPercentage: 0,
  clutchSideOutAttempts: 0, clutchSideOutWins: 0, clutchSideOutPercentage: 0,
  longestPointStreak: 0, longestSideOutStreak: 0, longestBreakStreak: 0, longestScoringDrought: 0,
  momentumSwingsCount: 0, secondServePoints: 0, secondServePointsWon: 0, secondServeSurvivalRate: 0,
  secondServeBreakPercentage: 0,
  firstServeRallies: 0, firstServeBreakWins: 0, secondServeRallies: 0, firstServeBreakPercentage: 0,
  firstServeReceiveRallies: 0, firstServeSideOutWins: 0, secondServeReceiveRallies: 0,
  secondServeSideOutWins: 0, firstServeAcesReceived: 0, secondServeAcesReceived: 0, firstServeSideOutPercentage: 0, secondServeSideOutPercentage: 0,
  pointsLostByDoubleFault: 0, comebackWins: 0, blownLeads: 0,
  setPointsWon: 0, setPointsBlown: 0, setPointsFaced: 0, setPointsSaved: 0,
  matchPointsWon: 0, matchPointsBlown: 0, matchPointsFaced: 0, matchPointsSaved: 0,
  hardCapPointsWon: 0, hardCapPointsLost: 0, timeoutResponsePointsWon: 0,
  timeoutResponsePointsPlayed: 0, timeoutResponseWinPercentage: 0, teamPointDifferential: 0,
  averageScoringRunLength: 0, biggestComebackDeficit: 0, firstTo5Attempts: 0, firstTo5Wins: 0,
  firstTo5WinRate: 0, firstTo10Attempts: 0, firstTo10Wins: 0, firstTo10WinRate: 0,
  closeSetWins: 0, closeSetLosses: 0, averageSetLength: 0,
  seriesLengthRecord: { '3-0': { wins: 0, losses: 0 }, '3-1': { wins: 0, losses: 0 }, '3-2': { wins: 0, losses: 0 } },
  currentFormWinPercentage: 0, hotColdStreak: 0, hotColdStreakType: 'none',
  dayOfWeekWinPercentage: {}, aceToDoubleFaultRatio: 0, faultRateUnderPressure: 0, pressureFaultDelta: 0,
  firstPointServeAttempts: 0, firstPointServeWinPercentage: 0, pointDifferential: 0, consistencyScore: 0, sampleSizeConfidence: 0,
  ...overrides,
});

const earnedBadge = (id: string, title: string, icon: string, tier: 'bronze' | 'silver' | 'gold'): PlayerBadge => ({
  id, title, icon, tier,
  category: 'milestone',
  description: title,
  earned: true,
  earnedAt: '2026-05-18T18:00:00.000Z',
  progress: 1,
  target: 1,
});

export interface DemoUltimateCard {
  displayName: string;
  seed: string;
  avatarUrl: string;
  rgx: number;
  stats: PlayerStatsSummary;
  badges: PlayerBadge[];
}

// RGX 1867 -> Pro tier (black iridescent card).
export const DEMO_CARD_PAUL: DemoUltimateCard = {
  displayName: 'Paul Schirop',
  seed: 'landing-paul-schirop',
  avatarUrl: 'https://pstaassets.blob.core.windows.net/assets/players/profile_PAU626-2022-06-17-05-35-22.jpg',
  rgx: 1867,
  stats: demoStats({
    playerId: 'landing-paul', displayName: 'Paul Schirop',
    matchesPlayed: 52, matchesWon: 42, matchWinPercentage: 81, pointsPlayed: 1640, pointsWon: 1028,
    breakAttempts: 820, breakWins: 382, aces: 209, breakPercentage: 72, aceRate: 25,
    sideOutAttempts: 820, sideOutWins: 584, rallyReceivePoints: 768, acesReceived: 28, sideOutPercentage: 76,
    doubleFaultRate: 4, faultRate: 8, defensiveServingPressure: 3, pressureScore: 54, consistencyScore: 88,
  }),
  badges: [
    earnedBadge('landing-paul-zap', 'Ace machine', 'zap', 'gold'),
    earnedBadge('landing-paul-shield', 'Defensive beast', 'shield-check', 'gold'),
    earnedBadge('landing-paul-trophy', 'Tour champion', 'trophy', 'gold'),
  ],
};

// RGX 1087 -> Basic tier (matte green card).
export const DEMO_CARD_GIACOMO: DemoUltimateCard = {
  displayName: 'Giacomo Nicotra',
  seed: 'landing-giacomo-nicotra',
  avatarUrl: 'https://pstaassets.blob.core.windows.net/assets/players/profile_GIA10857-2025-08-05-14-25-12.jpg',
  rgx: 1087,
  stats: demoStats({
    playerId: 'landing-giacomo', displayName: 'Giacomo Nicotra',
    matchesPlayed: 18, matchesWon: 8, matchWinPercentage: 44, pointsPlayed: 420, pointsWon: 205,
    breakAttempts: 210, breakWins: 74, aces: 19, breakPercentage: 42, aceRate: 9,
    sideOutAttempts: 210, sideOutWins: 111, rallyReceivePoints: 188, acesReceived: 12, sideOutPercentage: 59,
    doubleFaultRate: 6, faultRate: 10, defensiveServingPressure: 5, pressureScore: 49, consistencyScore: 64,
  }),
  badges: [
    earnedBadge('landing-giacomo-seedling', 'Fast learner', 'trending-up', 'bronze'),
    earnedBadge('landing-giacomo-shield', 'Side-Out builder', 'shield-check', 'bronze'),
    earnedBadge('landing-giacomo-users', 'Team regular', 'users', 'silver'),
  ],
};

// -- Demo match (point overview + player metrics) ----------------------------

const PLAYER_INES = { playerId: 'demo-ines', source: 'companion' as const, displayName: 'Ines' };
const PLAYER_FABIO = { playerId: 'demo-fabio', source: 'companion' as const, displayName: 'Fabio' };
const PLAYER_JOHANNA = { playerId: 'demo-johanna', source: 'companion' as const, displayName: 'Johanna' };
const PLAYER_PIA = { playerId: 'demo-pia', source: 'companion' as const, displayName: 'Pia' };

type DemoPointKind = 'rally' | 'ace' | 'doubleFault';
interface DemoPointAction {
  scoringTeamId: TeamId;
  kind?: DemoPointKind;
}

// Scores: set 1 ends 15-12, set 2 ends 15-13. The actions are intentionally fed through
// MatchEngine so the point overview uses the same serve sequence, first-server exception,
// player rotation, ace, side-out, break, and double-fault events as real matches.
const DEMO_SET_1: readonly DemoPointAction[] = [
  { scoringTeamId: 'A', kind: 'ace' },
  { scoringTeamId: 'B', kind: 'ace' },
  { scoringTeamId: 'A', kind: 'doubleFault' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B', kind: 'doubleFault' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A', kind: 'ace' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'A' },
];

const DEMO_SET_2: readonly DemoPointAction[] = [
  { scoringTeamId: 'B', kind: 'ace' },
  { scoringTeamId: 'A', kind: 'ace' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A', kind: 'doubleFault' },
  { scoringTeamId: 'B', kind: 'doubleFault' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'A', kind: 'ace' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B', kind: 'doubleFault' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B', kind: 'ace' },
  { scoringTeamId: 'A' },
  { scoringTeamId: 'B' },
  { scoringTeamId: 'A' },
];

const applyDemoPoint = (match: MatchState, action: DemoPointAction): MatchState => {
  const kind = action.kind ?? 'rally';
  if (kind === 'ace' && action.scoringTeamId === match.serve.servingTeamId) {
    return MatchEngine.recordAce(match, 'landing-demo').state;
  }
  if (kind === 'doubleFault' && action.scoringTeamId === match.serve.receivingTeamId) {
    const firstFault = MatchEngine.markServeFault(match, 'landing-demo').state;
    return MatchEngine.markServeFault(firstFault, 'landing-demo').state;
  }
  return MatchEngine.awardPoint(match, action.scoringTeamId, 'landing-demo').state;
};

const normalizeDemoEvents = (events: MatchEvent[]): MatchEvent[] => {
  const eventIdMap = new Map<string, string>();
  events.forEach((event, index) => eventIdMap.set(event.eventId, `landing-evt-${index + 1}`));
  const startMs = Date.parse('2026-06-06T17:05:00.000Z');

  return events.map((event, index) => {
    const payload = event.payload && typeof event.payload === 'object'
      ? { ...(event.payload as Record<string, unknown>) }
      : event.payload;
    if (payload && typeof payload === 'object' && 'linkedPointEventId' in payload) {
      const linkedPointEventId = payload['linkedPointEventId'];
      if (typeof linkedPointEventId === 'string') {
        payload['linkedPointEventId'] = eventIdMap.get(linkedPointEventId) ?? linkedPointEventId;
      }
    }
    return {
      ...event,
      eventId: eventIdMap.get(event.eventId) ?? `landing-evt-${index + 1}`,
      matchId: 'landing-demo-match',
      sequenceNumber: index + 1,
      createdAt: new Date(startMs + index * 45_000).toISOString(),
      createdByUid: 'landing-demo',
      deviceId: 'landing-demo',
      payload,
    };
  });
};

const buildDemoMatch = (): MatchState => {
  let match = MatchEngine.createMatch({
    ownerUid: 'landing-demo',
    mode: 'official',
    visibility: 'participants',
    config: { setsMode: 'bestOf', setCount: 3, sets: uniformSets(3, { pointsTo: 15, hardCap: null }), timeoutMinutes: 1, medicalTimeoutMinutes: 3, setPauseMinutes: 2 },
    teams: {
      A: { id: 'A', name: 'Ines & Fabio', player1: PLAYER_INES, player2: PLAYER_FABIO },
      B: { id: 'B', name: 'Johanna & Pia', player1: PLAYER_JOHANNA, player2: PLAYER_PIA },
    },
    startingTeamId: 'A',
    startingServerPlayerId: PLAYER_INES.playerId,
    startingReceiverPlayerId: PLAYER_JOHANNA.playerId,
  }).state;

  match = MatchEngine.startMatch(match, 'landing-demo', 'A', PLAYER_INES.playerId, PLAYER_JOHANNA.playerId).state;
  for (const point of DEMO_SET_1) {
    match = applyDemoPoint(match, point);
  }
  match = MatchEngine.startNextSet(match, 'landing-demo', 'B', PLAYER_JOHANNA.playerId, PLAYER_INES.playerId).state;
  for (const point of DEMO_SET_2) {
    match = applyDemoPoint(match, point);
  }

  const events = normalizeDemoEvents(match.events);
  return {
    ...match,
    matchId: 'landing-demo-match',
    ownerUid: 'landing-demo',
    participantUids: [],
    createdAt: '2026-06-06T17:00:00.000Z',
    startedAt: '2026-06-06T17:05:00.000Z',
    completedAt: '2026-06-06T17:55:00.000Z',
    updatedAt: '2026-06-06T17:55:00.000Z',
    events,
    source: 'web',
    dedupeKey: 'landing-demo-match',
  };
};

export const DEMO_MATCH: MatchState = buildDemoMatch();

const demoMatchStats = StatsEngine.buildPlayerStats([DEMO_MATCH], { completedOnly: false });
const demoMvpId = MvpEngine.selectForMatch(DEMO_MATCH)?.playerId ?? demoMatchStats[0]?.playerId ?? '';
const demoMvpStats = demoMatchStats.find((stat) => stat.playerId === demoMvpId) ?? demoMatchStats[0];

// This is the same builder fed the same per-match player summary as Match Review's individual
// breakdown. Keeping the landing preview on that path prevents staged labels or formulas drifting.
export const DEMO_SERVE_ARSENAL: ChartDefinition = ChartDataBuilder.polarServeArsenal(
  demoMvpStats ? [demoMvpStats, ...demoMatchStats.filter((stat) => stat !== demoMvpStats)] : [],
);

export interface DemoPlayerMetricRow {
  playerId: string;
  name: string;
  points: number;
  serves: number;
  receives: number;
  break: number;
  sideout: number;
  ace: number;
  pressure: number;
  diff: number;
  isMvp: boolean;
}

const demoPlayerById = new Map(allPlayers(DEMO_MATCH).map((player) => [player.playerId, player]));

export const DEMO_PLAYER_METRICS: DemoPlayerMetricRow[] = demoMatchStats
  .map((stat) => ({
    playerId: stat.playerId,
    name: demoPlayerById.get(stat.playerId)?.displayName ?? stat.displayName,
    points: stat.pointsWon,
    serves: stat.breakAttempts,
    receives: stat.sideOutAttempts,
    break: servePointWinPct(stat),
    sideout: receivePointWinPct(stat),
    ace: stat.aceRate,
    pressure: stat.pressureScore,
    diff: stat.pointDifferential,
    isMvp: stat.playerId === demoMvpId,
  }))
  .sort((a, b) => b.points - a.points);
